import { produce } from 'immer';
import { deleteAt, deleteIn, getIn, insertAt, setIn } from '../helpers/helpers';
import { KeyPath } from '../templateeditor/templateeditor-types';
import {
  TAGS_GET_REQUEST,
  TAGS_GET_FAILURE,
  TAGS_GET_SUCCESS,
  FORM_TAGS_GET_REQUEST,
  FORM_TAGS_GET_SUCCESS,
  FORM_TAGS_SAVE_REQUEST,
  FORM_TAGS_SAVE_SUCCESS,
  FORM_TAGS_SAVE_FAILURE,
  TAGS_ADD_REQUEST,
  TAGS_ADD_FAILURE,
  TAGS_DELETE_REQUEST,
  TAGS_DELETE_FAILURE,
  TAGS_RENAME_REQUEST,
  TAGS_RENAME_FAILURE,
  TAGS_MOVE_REQUEST,
  TAGS_MOVE_FAILURE,
  FORM_TAGS_ADD,
  FORM_TAGS_REMOVE,
  TAGS_ADD_SUCCESS,
  TAGS_DELETE_SUCCESS,
  TAGS_RENAME_SUCCESS,
  TAGS_MOVE_SUCCESS
} from './actions';
import { TagAction, TagsItem, TagPayload, TagState } from './tags-types';

const findTagKeyPath = (tags: TagsItem[], keyPath: KeyPath, tagId: string) => {
  if (tags) {
    for (let tagIndex = 0; tagIndex < tags.length; tagIndex++) {
      const tag = tags[tagIndex];
      if (tag.id === tagId) {
        return [...keyPath, tagIndex];
      }
      const childrenResult = findTagKeyPath(tag.children, [...keyPath, tagIndex, 'children'], tagId);
      if (childrenResult) {
        return childrenResult;
      }
    }
  }
  return null;
};

const addFormTag = (state: TagState, tagId: string, tagName: string) => {
  // if this tag is being re-added after deleting, then remove it from the deleted list
  let deletedFormTags = state.deletedFormTags || [];
  const deletedFormTagIndex = deletedFormTags.findIndex((x) => x === tagId);
  if (deletedFormTagIndex > -1) {
    deletedFormTags = deleteAt(deletedFormTags, deletedFormTagIndex);
    state.deletedFormTags = deletedFormTags;
  }

  let addedFormTags = state.addedFormTags || [];
  if (!addedFormTags.includes(tagId)) {
    addedFormTags = insertAt(addedFormTags, 0, tagId);
    state.addedFormTags = addedFormTags;
  }
  if (!state.formTags) {
    state.formTags = [];
  }
  if (!state.formTags.some((tag) => tagId === tag.tagId)) {
    state.formTags.push({ tagId, tagName });
  }
  return state;
};

const removeFormTag = (state: TagState, tagId: string) => {
  // if this tag is being deleted after being added, then remove it from the add list
  let addedFormTags = state.addedFormTags || [];
  const addedFormTagIndex = addedFormTags.findIndex((x) => x === tagId);
  if (addedFormTagIndex > -1) {
    addedFormTags = deleteAt(addedFormTags, addedFormTagIndex);
    state.addedFormTags = addedFormTags;
  }

  let deletedFormTags = state.deletedFormTags || [];
  if (!deletedFormTags.includes(tagId)) {
    deletedFormTags = insertAt(deletedFormTags, 0, tagId);
    state.deletedFormTags = deletedFormTags;
  }

  let formTags = state.formTags;
  const formTagIndex = formTags.findIndex((x) => x.tagId === tagId);
  formTags = deleteAt(formTags, formTagIndex);
  state.formTags = formTags;
  return state;
};

const addTag = (state: TagState, payload: TagPayload) => {
  const { savingTag, id, name } = payload;
  state.savingTag = savingTag;

  let items = state.items;
  items.push({ id, name, children: [] });
  items = items.sort((a, b) => a.name.localeCompare(b.name.toLowerCase()));
  state.items = items;

  return state;
};

const deleteTag = (state: TagState, id: string) => {
  state.savingTag = false;

  const tagKeyPath = findTagKeyPath(state.items, ['items'], id);
  if (tagKeyPath) {
    deleteIn(state, tagKeyPath);
  }

  let formTags = state.formTags;
  const formTagIndex = formTags.findIndex((x) => x.tagId === id);
  if (formTagIndex > -1) {
    formTags = deleteAt(formTags, formTagIndex);
    state.formTags = formTags;
  }

  return state;
};

const renameTag = (state: TagState, id: string, name: string) => {
  state.savingTag = false;

  const tagKeyPath = findTagKeyPath(state.items, ['items'], id);
  if (tagKeyPath) {
    setIn(state, [...tagKeyPath, 'name'], name);
  }

  const formTagIndex = state.formTags.findIndex((x) => x.tagId === id);
  if (formTagIndex > -1) {
    setIn(state, ['formTags', formTagIndex, 'tagName'], name);
  }

  return state;
};

const moveTag = (state: TagState, id: string, parentId: string) => {
  state.savingTag = false;

  const previousTagKeyPath = findTagKeyPath(state.items, ['items'], id);
  const tag = getIn(state, previousTagKeyPath);
  deleteIn(state, previousTagKeyPath);

  let newKeyPath = null;
  if (parentId) {
    const parentTagKeyPath = findTagKeyPath(state.items, ['items'], parentId);
    newKeyPath = [...parentTagKeyPath, 'children'];
  } else {
    newKeyPath = ['items'];
  }

  let items: TagsItem[] = getIn(state, newKeyPath) || [];
  insertAt(items, 0, tag);
  items = items.slice().sort((a, b) => a.name.localeCompare(b.name.toLowerCase()));
  setIn(state, newKeyPath, items);

  return state;
};

const forms = (existingState = {}, action: TagAction): TagState => {
  if (!action) return existingState;

  return produce(existingState, (state: TagState) => {
    switch (action.type) {
      case TAGS_GET_REQUEST:
      case TAGS_GET_FAILURE:
      case TAGS_GET_SUCCESS:
      case FORM_TAGS_GET_REQUEST:
      case FORM_TAGS_GET_SUCCESS:
      case FORM_TAGS_SAVE_REQUEST:
      case FORM_TAGS_SAVE_SUCCESS:
      case FORM_TAGS_SAVE_FAILURE:
      case TAGS_ADD_REQUEST:
      case TAGS_ADD_FAILURE:
      case TAGS_DELETE_REQUEST:
      case TAGS_DELETE_FAILURE:
      case TAGS_RENAME_REQUEST:
      case TAGS_RENAME_FAILURE:
      case TAGS_MOVE_REQUEST:
      case TAGS_MOVE_FAILURE:
        return { ...state, ...action.payload };

      case FORM_TAGS_ADD:
        return addFormTag(state, action.payload.tagId, action.payload.tagName);

      case FORM_TAGS_REMOVE:
        return removeFormTag(state, action.payload.tagId);

      case TAGS_ADD_SUCCESS:
        return addTag(state, action.payload);

      case TAGS_DELETE_SUCCESS:
        return deleteTag(state, action.payload.id);

      case TAGS_RENAME_SUCCESS:
        return renameTag(state, action.payload.id, action.payload.name);

      case TAGS_MOVE_SUCCESS:
        return moveTag(state, action.payload.id, action.payload.parentId);

      default:
        return state;
    }
  });
};

export default forms;
